import { ImagePaths } from '../../lib/imageUrls.js';

export const ai200AzureAiDeveloperAssociate = {

    id: 20,
    slug: 'ai-200-azure-ai-developer-associate',
    ogImage: '/og/courses/ai-200-azure-ai-developer-associate.jpg',
    courseCode: 'AI-200-001',
    featured: false,
    title: 'AI-200: Azure AI Developer Associate',
    subtitle: 'Build production-ready AI applications on Azure',

    valueProposition: 'Develop enterprise-grade AI applications using Azure OpenAI Service, Computer Vision, and Language Services to deliver generative AI solutions, agentic systems, and intelligent automation for Australian organisations.',

    duration: '24 hours',
    students: 'New',
    level: 'Intermediate',
    organization: 'Microsoft',
    image: ImagePaths.COURSE_1,
    shortDescription: 'AI-200-001 — Azure AI developer training covering Azure OpenAI, computer vision, and responsible AI for Australian software engineers.',

    price: {
      aud: 299,
      currency: 'AUD',
      earlyBird: 249
    },

    stripeConfig: {
    priceId: 'price_1T9bE6Ku1nyc322UwU4BBLqw',
    productId: 'prod_U7qvRcZplBVdPD'
  },

    snapshot: {
      whoItsFor: [
        'Software developers building AI-powered applications',
        'Cloud engineers implementing Azure AI services',
        'Solution architects designing intelligent systems',
        'Data engineers integrating AI into data pipelines',
        'DevOps professionals deploying AI solutions',
        'IT professionals preparing for AI-102 certification'
      ],
      accreditation: 'Preparation for Microsoft AI-102: Azure AI Engineer Associate Certification'
    },

    logistics: {
      classSize: 'Maximum 16 participants for intensive hands-on labs',
      requirements: [
        'Computer with modern web browser and development environment',
        'Active Microsoft Azure subscription (free tier acceptable)',
        'Visual Studio Code or preferred IDE installed',
        'Reliable internet connection (minimum 15 Mbps)',
        'Basic programming experience in Python or C# required',
        'Familiarity with REST APIs and JSON helpful'
      ]
    },

    overview: 'Build production-ready AI applications on Microsoft Azure in this comprehensive 24-hour developer-focused course. You\'ll gain hands-on experience implementing generative AI solutions with Azure OpenAI Service, creating intelligent agents with tool-calling capabilities, and deploying computer vision and natural language processing systems. Through practical labs and real-world scenarios, you\'ll learn to architect secure, scalable AI solutions while implementing responsible AI practices and content safety controls aligned with Australian Privacy Principles. Perfect for developers and engineers preparing for the Microsoft AI-102 certification or building enterprise AI capabilities for Australian organisations.',

    outcomes: [
      'Develop generative AI applications using Azure OpenAI Service with token optimisation and prompt engineering',
      'Implement agentic AI solutions with function calling, tool orchestration, and multi-agent systems',
      'Build computer vision solutions using Azure Computer Vision, Custom Vision, and Video Indexer services',
      'Create natural language processing applications with sentiment analysis, translation, and language understanding',
      'Deploy knowledge mining solutions using Azure AI Search with vector-enabled semantic search capabilities',
      'Implement Document Intelligence for automated form processing and data extraction workflows',
      'Apply responsible AI principles and content safety controls including filters, blocklists, and prompt shields',
      'Design secure, monitored AI architectures compliant with Australian Privacy Principles and ISO 27001 standards'
    ],

    audience: [
      'Software developers and application engineers',
      'Cloud solution architects and engineers',
      'Data engineers and AI specialists',
      'DevOps and platform engineers',
      'IT professionals transitioning to AI development',
      'Developers preparing for AI-102 certification',
      'Technical leads implementing enterprise AI solutions'
    ],

    prerequisites: [
      'Programming experience in Python or C# (intermediate level)',
      'Understanding of REST APIs and JSON data formats',
      'Basic knowledge of cloud computing concepts',
      'Familiarity with Azure fundamentals (or AI-901 equivalent)',
      'Experience with version control (Git) helpful',
      'Basic understanding of machine learning concepts beneficial'
    ],

    modules: [
      {
        title: 'Plan and Manage an Azure AI Solution',
        duration: '5 hours',
        topics: [
          'Provisioning Azure AI services and Microsoft AI Foundry portal',
          'Managing authentication, keys, and managed identities',
          'Implementing network security and private endpoints',
          'Monitoring AI service usage, costs, and performance metrics',
          'Configuring diagnostic logging and Application Insights',
          'Resource planning and capacity management',
          'Multi-region deployment strategies'
        ]
      },
      {
        title: 'Implement Generative AI Solutions',
        duration: '5 hours',
        topics: [
          'Building applications with Azure OpenAI Service',
          'Deploying and managing GPT models',
          'Advanced prompt engineering and system message design',
          'Token management, optimisation, and cost control',
          'Implementing streaming responses and chat completions',
          'Function calling and structured outputs',
          'Integrating embeddings for semantic search',
          'Fine-tuning custom models for domain-specific use cases'
        ]
      },
      {
        title: 'Implement an Agentic AI Solution',
        duration: '3 hours',
        topics: [
          'Understanding AI agents and autonomous systems',
          'Creating custom agents with tool-calling capabilities',
          'Implementing function definitions and execution workflows',
          'Building multi-agent orchestration systems',
          'Managing agent memory and conversation state',
          'Integrating agents with external APIs and data sources',
          'Testing and debugging agentic workflows'
        ]
      },
      {
        title: 'Computer Vision and NLP Solutions',
        duration: '6 hours',
        topics: [
          'Image analysis with Azure Computer Vision API',
          'Object detection and classification with Custom Vision',
          'Face detection and facial recognition services',
          'Video analysis using Azure Video Indexer',
          'Text analytics and sentiment analysis',
          'Language translation with Azure Translator',
          'Named entity recognition and key phrase extraction',
          'Conversational language understanding (CLU)'
        ]
      },
      {
        title: 'Knowledge Mining and Document Intelligence',
        duration: '3 hours',
        topics: [
          'Implementing Azure AI Search for knowledge mining',
          'Creating search indexes with enrichment pipelines',
          'Configuring vector search and semantic ranking',
          'Building custom skills and cognitive enrichments',
          'Document Intelligence for form and document processing',
          'Custom model training for invoice and receipt extraction',
          'Integrating search with Power BI and applications'
        ]
      },
      {
        title: 'Responsible AI and Content Safety',
        duration: '2 hours',
        topics: [
          'Implementing Azure AI Content Safety service',
          'Configuring content filters for harmful content detection',
          'Managing blocklists and custom filtering rules',
          'Implementing prompt shields for jailbreak protection',
          'Monitoring and auditing AI system outputs',
          'Applying responsible AI principles to Australian Privacy Principles',
          'Compliance considerations for ISO 27001 and Essential Eight',
          'Incident response for AI safety events'
        ]
      }
    ],

    format: {
      type: 'Live online with extensive hands-on labs',
      includes: [
        '24 hours of instructor-led training over 3 days',
        'Comprehensive hands-on labs with real Azure AI services',
        'Azure AI developer toolkit and code samples',
        'AI-102 exam preparation materials and practice tests',
        'Architecture templates for common AI patterns',
        'Responsible AI implementation checklist',
        'Certificate of completion',
        '6 months access to course materials'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Three-day intensive format (8 hours per day) or six half-days for working professionals'
    },

    // Pathway relationships
    pathways: [
      'ai-foundations-professional',
      'azure-ai-engineer-certification'
    ],

    skillsTaught: [
      'Azure OpenAI Service',
      'Generative AI applications',
      'Agentic AI systems',
      'Computer Vision on Azure',
      'Knowledge mining and search',
      'Responsible AI and content safety'
    ],

    hasCapstone: true,

    nextCourses: [
      'ai-300-ml-ops-engineer-associate',
      'ai-master-prompt-engineering'
    ],

    faqs: [
      {
        question: 'Do I need to be an experienced developer to take this course?',
        answer: 'Yes, this is an intermediate-level course requiring programming experience in Python or C#. You should be comfortable writing code, working with APIs, and understanding JSON. If you\'re new to programming, consider starting with foundational programming courses before enrolling.'
      },
      {
        question: 'What programming language should I know?',
        answer: 'The course supports both Python and C#. Most examples use Python as it\'s the most common language for AI development, but C# developers are equally welcome. You should have intermediate proficiency in at least one of these languages.'
      },
      {
        question: 'Does this course prepare me for the AI-102 certification?',
        answer: 'Yes, this course comprehensively covers all AI-102: Azure AI Engineer Associate exam objectives. You\'ll receive exam preparation materials, practice questions, and hands-on experience with every exam topic. Most participants report strong confidence and high pass rates after completing this training.'
      },
      {
        question: 'Will I need to pay for Azure services during the labs?',
        answer: 'Most labs can be completed within Azure free tier limits. We provide guidance on cost management and resource cleanup. For extended experimentation beyond the course, expect minimal costs (typically under $20 AUD). We teach cost optimisation as part of the curriculum.'
      },
      {
        question: 'How does this course address Australian compliance and security requirements?',
        answer: 'We specifically cover implementing AI solutions aligned with Australian Privacy Principles (APPs), Essential Eight security controls, and ISO 27001 standards. You\'ll learn to architect secure AI systems with data sovereignty considerations, implement content safety controls, and meet Australian regulatory requirements for AI deployment.'
      },
      {
        question: 'What\'s the difference between AI-901 and AI-200?',
        answer: 'AI-901 is a foundational course for understanding Azure AI services without coding. AI-200 is an intermediate developer course requiring programming skills to build production AI applications. If you\'re a developer, you can start with AI-200 directly if you have basic Azure knowledge.'
      },
      {
        question: 'Can this course be customised for our organisation\'s specific AI use cases?',
        answer: 'Absolutely. We offer tailored in-house delivery where we adapt labs and examples to your industry, use cases, and tech stack. This is ideal for development teams building specific AI capabilities. Contact us to discuss customisation options for your organisation.'
      },
      {
        question: 'What support is available after completing the course?',
        answer: 'You\'ll receive 6 months access to all materials. We also offer consulting services for organisations implementing enterprise AI solutions.'
      }
    ]
};
